import { QUERY } from 'api/Query';
import type { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import type { ReactNode } from 'react';
import type { LineSegment } from 'ts/commons/code/RenderedCode.segments';
import type { SinglePreprocessorExpansionTransport } from 'typedefs/SinglePreprocessorExpansionTransport';

/** Description of a search result. */
export type InCodeSearchResult = {
	line: number;
	startOffset: number;
	endOffset: number;
	macroMatches: Map<
		number,
		{
			startOffset: number;
			endOffset: number;
		}
	>;
};

/** Searches in the given lines for the given search term. */
export async function search(
	project: string,
	uniformPath: string,
	t: UnresolvedCommitDescriptor,
	searchTerm: string,
	linesWithTokens: Map<number, LineSegment[]>,
	expansionGroupIds: number[]
) {
	let expansions: SinglePreprocessorExpansionTransport[] = [];

	if (expansionGroupIds.length > 0) {
		expansions = await QUERY.getSinglePreprocessorExpansion(project, uniformPath, {
			t,
			expansionGroupIds
		}).fetch();
	}

	return Array.from(linesWithTokens.entries()).flatMap(([lineNumber, tokens]) =>
		searchLine(Number(lineNumber), tokens, expansions, searchTerm)
	);
}

function searchLine(
	lineNumber: number,
	segments: LineSegment[],
	expansions: SinglePreprocessorExpansionTransport[],
	searchTerm: string
): InCodeSearchResult[] {
	const results: InCodeSearchResult[] = [];

	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i]!;

		const expansion = getExpansionForSegment(segment, expansions);
		if (expansion) {
			if (isLastExpandedSegment(segment, expansion)) {
				results.push(...searchInExpansion(lineNumber, segments, expansions, searchTerm, i, expansion));
			}
			continue;
		}

		const firstChar = searchTerm.charAt(0);
		let index = segment.text.toLowerCase().indexOf(firstChar.toLowerCase());

		while (index >= 0) {
			const match = tryMatch(segments, expansions, searchTerm, i, index);
			if (match?.success) {
				results.push({
					line: lineNumber,
					startOffset: segment.offsetInFile + index,
					endOffset: match.endOffset,
					macroMatches: match.macroMatches
				});
			}

			index = segment.text.toLowerCase().indexOf(firstChar.toLowerCase(), index + 1);
		}
	}

	return results;
}

function searchInExpansion(
	lineNumber: number,
	segments: LineSegment[],
	expansions: SinglePreprocessorExpansionTransport[],
	searchTerm: string,
	segmentIndex: number,
	currentExpansion: SinglePreprocessorExpansionTransport
): InCodeSearchResult[] {
	const results: InCodeSearchResult[] = [];

	const firstChar = searchTerm.charAt(0);
	let index = currentExpansion.replacementText.indexOf(firstChar);

	while (index >= 0) {
		const match = tryMatch(
			[{ text: currentExpansion.replacementText, offsetInFile: 0, fileAndOffsetId: '' }],
			[],
			searchTerm,
			0,
			index
		);

		if (!match) {
			index = currentExpansion.replacementText.indexOf(firstChar, index + 1);
			continue;
		}

		const macroMatches = new Map<
			number,
			{
				startOffset: number;
				endOffset: number;
			}
		>();

		const currentSegment = segments[segmentIndex]!;
		macroMatches.set(currentSegment.offsetInFile, {
			startOffset: index,
			endOffset: match.endOffset
		});

		if (match.success) {
			// The complete searchTerm is in the marco expansion
			results.push({
				line: lineNumber,
				startOffset: currentSegment.offsetInFile,
				endOffset: currentSegment.offsetInFile + currentSegment.text.length,
				macroMatches
			});
		} else {
			// The start of the searchTerm got matched
			// Try to match the tail in the following tokens
			const matchTail = tryMatch(
				segments,
				expansions,
				searchTerm.substring(match.endOffset - index),
				segmentIndex + 1,
				0
			);
			if (matchTail?.success) {
				matchTail.macroMatches.forEach((value, key) => macroMatches.set(key, value));

				results.push({
					line: lineNumber,
					startOffset: currentSegment.offsetInFile,
					endOffset: matchTail.endOffset,
					macroMatches
				});
			}
		}

		index = currentExpansion.replacementText.indexOf(firstChar, index + 1);
	}

	return results;
}

function tryMatch(
	segments: LineSegment[],
	expansions: SinglePreprocessorExpansionTransport[],
	searchTerm: string,
	segmentIndex: number,
	charIndex: number
) {
	let pos = 0;

	const macroMatches = new Map<
		number,
		{
			startOffset: number;
			endOffset: number;
		}
	>();

	if (segments.length === 0) {
		return {
			endOffset: 0,
			success: false,
			macroMatches
		};
	}

	while (segmentIndex < segments.length) {
		const segment = segments[segmentIndex]!;

		const expansion = getExpansionForSegment(segment, expansions);
		if (expansion) {
			if (!isLastExpandedSegment(segment, expansion)) {
				// Skip the segment which will not be rendered
				segmentIndex += 1;
				charIndex = 0;
				continue;
			}

			const match = tryMatch(
				[{ text: expansion.replacementText, offsetInFile: 0, fileAndOffsetId: '' }],
				[],
				searchTerm.substring(pos),
				0,
				0
			);
			if (match === null) {
				return null;
			}
			macroMatches.set(segment.offsetInFile, {
				startOffset: 0,
				endOffset: match.endOffset
			});

			if (match.success) {
				// We finished our match in the macro
				return {
					endOffset: segment.offsetInFile + segment.text.length,
					success: true,
					macroMatches
				};
			}

			pos += match.endOffset;
			segmentIndex += 1;
			charIndex = 0;
			continue;
		}

		while (pos < searchTerm.length && charIndex < segment.text.length) {
			if (searchTerm.charAt(pos).toLowerCase() !== segment.text.charAt(charIndex).toLowerCase()) {
				return null;
			}

			charIndex += 1;
			pos += 1;
		}

		if (pos === searchTerm.length) {
			return {
				endOffset: segment.offsetInFile + charIndex,
				success: true,
				macroMatches
			};
		}

		segmentIndex += 1;
		charIndex = 0;
	}

	const lastSegment = segments[segments.length - 1]!;
	return {
		endOffset: lastSegment.offsetInFile + lastSegment.text.length,
		success: false,
		macroMatches
	};
}

function getExpansionForSegment(
	segment: LineSegment,
	expansions: SinglePreprocessorExpansionTransport[]
): SinglePreprocessorExpansionTransport | null {
	return (
		expansions.find(expansion => expansion.offsetsOfReplacedOriginalTokens.includes(segment.offsetInFile)) ?? null
	);
}

/** The last segment of an expansion is important, because that one gets expanded. */
function isLastExpandedSegment(segment: LineSegment, expansion: SinglePreprocessorExpansionTransport): boolean {
	return segment.offsetInFile === Math.max(...expansion.offsetsOfReplacedOriginalTokens);
}

/** Highlights the given segment corresponding to the current search results. */
export function highlightCodeSearch(
	segment: LineSegment,
	searchResults: InCodeSearchResult[],
	highlightedSearchResult: number | undefined,
	text?: string
) {
	const displayText = text ?? segment.text;
	const selected = searchResults[highlightedSearchResult!]!;

	const relevant = searchResults
		.filter(result => isHighlightedBy(segment, result))
		.sort((a, b) => a.startOffset - b.endOffset);

	if (relevant.length === 0) {
		return displayText;
	}
	const highlightBorders = createHighlightBorders(relevant, segment, displayText, selected);

	const parts: ReactNode[] = [];

	for (let i = 0; i < highlightBorders.length; i++) {
		const endOfPrev = highlightBorders[i - 1]?.end ?? 0;
		const cur = highlightBorders[i]!;

		parts.push(
			displayText.substring(endOfPrev, cur.start),
			<span
				key={'highlight-' + segment.offsetInFile + '-' + cur.start + '-' + cur.end}
				className={cur.selected ? 'bg-[#fbff79]' : 'bg-[#edc105]'}
			>
				{displayText.substring(cur.start, cur.end)}
			</span>
		);
	}
	parts.push(displayText.substring(highlightBorders[highlightBorders.length - 1]!.end));

	return <>{parts}</>;
}

function isHighlightedBy(segment: LineSegment, searchResult: InCodeSearchResult): boolean {
	const start1 = segment.offsetInFile;
	const end1 = segment.offsetInFile + segment.text.length;
	const start2 = searchResult.startOffset;
	const end2 = searchResult.endOffset;

	if (end1 - start1 < end2 - start2) {
		return (start2 <= start1 && start1 <= end2) || (start2 <= end1 && end1 <= end2);
	}
	return (start1 <= start2 && start2 <= end1) || (start1 <= end2 && end2 <= end1);
}

function createHighlightBorders(
	relevant: InCodeSearchResult[],
	segment: LineSegment,
	displayText: string,
	selected: InCodeSearchResult
) {
	const highlightBorders: Array<{
		start: number;
		end: number;
		selected: boolean;
	}> = [];

	for (const result of relevant) {
		const start = Math.max(result.startOffset - segment.offsetInFile, 0);
		const end = Math.min(result.endOffset - segment.offsetInFile, displayText.length);
		const isSelected = result === selected;

		const last = highlightBorders[highlightBorders.length - 1];
		if (last === undefined || last.end <= start) {
			// Highlighting does not interact with the previous highlighting
			highlightBorders.push({
				start,
				end,
				selected: isSelected
			});
		} else if (isSelected) {
			// Overwrite the previous highlighting with selected highlighting
			highlightBorders[highlightBorders.length - 1] = {
				start: last.start,
				end: start,
				selected: last.selected
			};
			highlightBorders.push({
				start,
				end,
				selected: isSelected
			});
		} else {
			// Increase the length of the existing highlighting
			highlightBorders[highlightBorders.length - 1] = {
				start: last.start,
				end,
				selected: isSelected
			};
		}
	}
	return highlightBorders;
}
